import React from 'react';
import {
    Box,
    Typography,
    Button,
    Paper,
    Divider,
    Grid,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ReceiptIcon from '@mui/icons-material/Receipt';
import SchoolIcon from '@mui/icons-material/School';
import { Order } from '../../../services/orderService';
import orderService from '../../../services/orderService';
import { useTranslation } from '../../../i18n';

interface ConfirmationStepProps {
    order: Order;
    onViewOrders: () => void;
    onContinueShopping: () => void;
}

const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
    order,
    onViewOrders,
    onContinueShopping,
}) => {
    const { t } = useTranslation();

    return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon
                sx={{ fontSize: 80, color: 'success.main', mb: 2 }}
            />

            <Typography variant="h4" gutterBottom>
                {t('checkout.confirmation.title')}
            </Typography>

            <Typography variant="h6" color="text.secondary" gutterBottom>
                {t('checkout.confirmation.thankYou')}
            </Typography>

            <Paper sx={{ p: 3, my: 4, maxWidth: 500, mx: 'auto' }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    {t('checkout.confirmation.orderNumber', { orderNumber: order.orderNumber })}
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Box sx={{ textAlign: 'left' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Gekaufte Produkte:
                    </Typography>

                    {order.items.map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                py: 1,
                            }}
                        >
                            <Typography variant="body2">
                                {item.title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {parseFloat(item.total) === 0
                                    ? 'Kostenlos'
                                    : orderService.formatTotal({ ...order, total: item.total })
                                }
                            </Typography>
                        </Box>
                    ))}

                    <Divider sx={{ my: 2 }} />

                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                            Gesamtsumme
                        </Typography>
                        <Typography variant="subtitle1" fontWeight="bold" color="primary">
                            {orderService.formatTotal(order)}
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                {t('checkout.confirmation.emailSent', { email: order.customerEmail })}
            </Typography>

            <Grid container spacing={2} justifyContent="center">
                <Grid>
                    <Button
                        variant="contained"
                        startIcon={<SchoolIcon />}
                        onClick={onContinueShopping}
                        size="large"
                    >
                        Zu meinen Kursen
                    </Button>
                </Grid>
                <Grid>
                    <Button
                        variant="outlined"
                        startIcon={<ReceiptIcon />}
                        onClick={onViewOrders}
                        size="large"
                    >
                        {t('checkout.confirmation.viewOrders')}
                    </Button>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ConfirmationStep;
